import type { PeakData } from "../waveform/peaks.js";

/**
 * Amplitude below which a bucket counts as silence, as a fraction of full
 * scale. Deliberately low: the aim is to skip genuine tails, not to clip off
 * quiet endings that are still music.
 */
const SILENCE_THRESHOLD = 0.02;

export interface Bounds {
  /** First moment with audible content, in seconds. */
  start: number;
  /** Last moment with audible content, in seconds. */
  end: number;
}

/**
 * Find where a track actually starts and stops sounding (X11b).
 *
 * Many tracks end in a long fade or trailing silence, which auto-mix would
 * otherwise mix over as though it were music — the transition would begin
 * during the fade and the next track would arrive over nothing.
 *
 * This is a cheap amplitude test over peak data already fetched for the deck,
 * explicitly not structural analysis: it finds silence, not outros.
 */
export function effectiveBounds(
  peaks: PeakData,
  duration: number,
  threshold = SILENCE_THRESHOLD,
): Bounds {
  const limit = threshold * 127;
  const { values, count } = peaks;

  const loud = (bucket: number): boolean => {
    const low = values[bucket * 2] ?? 0;
    const high = values[bucket * 2 + 1] ?? 0;
    return Math.max(Math.abs(low), Math.abs(high)) > limit;
  };

  let first = 0;
  while (first < count && !loud(first)) first++;

  let last = count - 1;
  while (last > first && !loud(last)) last--;

  // Entirely silent, or unreadable: treat the whole track as content rather
  // than returning a zero-length window that would break planning.
  if (first >= count || last <= first) {
    return { start: 0, end: duration };
  }

  const perBucket = peaks.bucketsPerSecond > 0 ? 1 / peaks.bucketsPerSecond : duration / count;
  return {
    start: Math.max(0, first * perBucket),
    // +1 so the bound covers the whole of the last audible bucket.
    end: Math.min(duration, (last + 1) * perBucket),
  };
}

export interface Segment {
  start: number;
  end: number;
  /** True when the track was longer than the cap and a region was chosen. */
  excerpted: boolean;
}

/**
 * Pick the region of a track to play.
 *
 * Short tracks play whole. A track longer than `maxSeconds` plays a randomly
 * placed excerpt of that length instead, so a single very long piece does not
 * occupy the mix for its entire duration — the 23-minute ambient track in this
 * library would otherwise be most of an hour's listening on its own.
 *
 * The excerpt is snapped to a bar line where a grid exists, so an excerpt of a
 * rhythmic track still starts somewhere musically sensible.
 */
export function chooseSegment(
  bounds: Bounds,
  maxSeconds: number,
  beats: readonly number[] = [],
  beatsPerBar = 4,
  random: () => number = Math.random,
): Segment {
  const audible = bounds.end - bounds.start;
  if (!(maxSeconds > 0) || audible <= maxSeconds) {
    return { start: bounds.start, end: bounds.end, excerpted: false };
  }

  const latestStart = bounds.end - maxSeconds;
  let start = bounds.start + random() * (latestStart - bounds.start);

  // Snap to a bar line so an excerpt does not begin mid-phrase.
  if (beats.length > beatsPerBar) {
    let best: number | null = null;
    for (let i = 0; i < beats.length; i += beatsPerBar) {
      const beat = beats[i] as number;
      if (beat < bounds.start || beat > latestStart) continue;
      if (best === null || Math.abs(beat - start) < Math.abs(best - start)) best = beat;
    }
    if (best !== null) start = best;
  }

  return { start, end: start + maxSeconds, excerpted: true };
}
